// Instance Admin → Edge & HTTPS → Servers: the registry of nginx / Caddy
// servers Twasta delivers configuration and certificates to. Each server runs
// the standalone edge agent holding only its own token; the hub records every
// delivery, so a server that falls behind shows here (and on the Propagation
// tab, per project) until it catches up.

import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import {
  Plus, RefreshCw, Send, Stethoscope, History, KeyRound, Trash2, Pencil, Download, Power,
  Terminal, Check, Loader2, Copy,
} from 'lucide-react'
import {
  addEdgeServer, checkEdgeServer, downloadEdgeAgent, getEdgeServerDeliveries, getEdgeServers,
  newEdgeInstallLink, pushEdgeServer, removeEdgeServer, rotateEdgeServerToken,
  setEdgeServerEnabled, updateEdgeServer,
  type EdgeDeliveryInfo, type EdgeInstall, type EdgeInstallLink, type EdgeServerInfo,
  type EdgeServerInput,
} from '@/api/instanceAdmin'
import { AdminModal, Field, InfoBox, Td, Th, chipStyle, errText, tableStyle } from './adminUi'

export const SERVER_STATUS: Record<EdgeServerInfo['status'], { label: string; color: string }> = {
  new: { label: 'Never connected', color: 'var(--color-text-secondary)' },
  in_sync: { label: 'In sync', color: 'var(--color-success)' },
  pending: { label: 'Updating…', color: 'var(--color-info)' },
  failed: { label: 'Failed', color: 'var(--color-error)' },
  offline: { label: 'Offline', color: 'var(--color-error)' },
  disabled: { label: 'Disabled', color: 'var(--color-text-faint)' },
}

export function fmtTime(iso: string | null): string {
  if (!iso) return '—'
  try { return new Date(iso.endsWith('Z') ? iso : `${iso}Z`).toLocaleString() } catch { return iso }
}

async function saveAgentFile() {
  try {
    const blob = await downloadEdgeAgent()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'twasta_edge_agent.py'
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    toast.error(errText(e, 'Download failed'))
  }
}

export default function EdgeServersPanel({ mode }: { mode: string }) {
  const [servers, setServers] = useState<EdgeServerInfo[]>([])
  const [revision, setRevision] = useState(0)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<EdgeServerInfo | 'new' | null>(null)
  const [install, setInstall] = useState<{ server: EdgeServerInfo; install: EdgeInstall } | null>(null)
  const [link, setLink] = useState<{ server: EdgeServerInfo; install_link: EdgeInstallLink } | null>(null)
  const [history, setHistory] = useState<EdgeServerInfo | null>(null)
  const [busy, setBusy] = useState<number | null>(null)

  const refresh = useCallback(async () => {
    try {
      const r = await getEdgeServers()
      setServers(r.servers)
      setRevision(r.revision)
    } catch (e) {
      toast.error(errText(e, 'Failed to load servers'))
    } finally { setLoading(false) }
  }, [])

  useEffect(() => {
    void refresh()
    const t = window.setInterval(() => { void refresh() }, 10000)
    return () => window.clearInterval(t)
  }, [refresh])

  const act = async (s: EdgeServerInfo, fn: () => Promise<unknown>, done?: string) => {
    setBusy(s.id)
    try {
      await fn()
      if (done) toast.success(done)
    } catch (e) {
      toast.error(errText(e, 'Failed'))
    } finally {
      setBusy(null)
      void refresh()
    }
  }

  const rotate = (s: EdgeServerInfo) => {
    if (!window.confirm(`Issue a new token for ${s.name}?\n\nThe current token stops working immediately; the agent on that server needs the new agent.json before it can receive anything again.`)) return
    void act(s, async () => setInstall(await rotateEdgeServerToken(s.id)))
  }
  const remove = (s: EdgeServerInfo) => {
    if (!window.confirm(`Remove ${s.name} from the registry?\n\nTwasta stops delivering to it. Whatever it already serves keeps running until you stop nginx/Caddy there.`)) return
    void act(s, () => removeEdgeServer(s.id), 'Server removed')
  }

  if (loading) return <div style={{ color: 'var(--color-text-secondary)' }}>Loading…</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {mode === 'external' && (
        <InfoBox>
          The edge mode is <strong>External</strong>, so nothing is delivered to these servers.
          Choose nginx/Caddy or Cloudflare on the <strong>Settings</strong> tab to start delivering.
        </InfoBox>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <span style={chipStyle}>current revision {revision}</span>
        <span style={chipStyle}>
          {servers.filter((s) => s.enabled && s.in_sync).length} of {servers.filter((s) => s.enabled).length} in sync
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => void saveAgentFile()}>
            <Download size={13} style={{ marginRight: 4, verticalAlign: '-2px' }} />Download agent
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => void refresh()} title="Refresh">
            <RefreshCw size={13} />
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
            <Plus size={13} style={{ marginRight: 4, verticalAlign: '-2px' }} />Add server
          </button>
        </div>
      </div>

      {servers.length === 0 ? (
        <InfoBox>
          No web servers yet. Click <strong>Add server</strong>, give it a name, and run the one
          command shown on that machine (start with this Twasta server itself). The command
          installs nginx and the agent and connects the server; it then appears here as{' '}
          <strong>In sync</strong>.
        </InfoBox>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr><Th>Server</Th><Th>Connection</Th><Th>Status</Th><Th>Revision</Th><Th>Last contact</Th><Th /></tr>
          </thead>
          <tbody>
            {servers.map((s) => {
              const st = SERVER_STATUS[s.status] || SERVER_STATUS.new
              return (
                <tr key={s.id} style={{ opacity: s.enabled ? 1 : 0.6 }}>
                  <Td>
                    <div style={{ fontWeight: 600 }}>
                      {s.name}
                      {s.is_local && <span style={{ ...chipStyle, marginLeft: 6, fontWeight: 400 }}>this Twasta server</span>}
                    </div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-faint)' }}>
                      {s.engine}{s.engine_version ? ` · ${s.engine_version.replace(/^nginx version: /, '')}` : ''}
                      {s.agent_version ? ` · agent ${s.agent_version}` : ''}
                    </div>
                    {s.description && <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>{s.description}</div>}
                  </Td>
                  <Td>
                    {s.connection === 'push' ? 'Push' : 'Pull'}
                    <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-faint)' }}>
                      {s.connection === 'push' ? s.agent_url : 'agent connects to Twasta'}
                    </div>
                  </Td>
                  <Td style={{ maxWidth: 300 }}>
                    <span style={{ ...chipStyle, color: st.color, borderColor: st.color }}>{st.label}</span>
                    {s.last_error && s.status !== 'in_sync' && (
                      <pre style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap', fontSize: '0.8125rem', color: 'var(--color-error)' }}>
                        {s.last_error}
                      </pre>
                    )}
                    {s.install_pending && !s.last_contact_at && (
                      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-faint)' }}>
                        waiting for the install command to be run
                      </div>
                    )}
                    {s.last_warning && s.status === 'in_sync' && (
                      <pre title="nginx accepted the configuration but warned" style={{
                        margin: '4px 0 0', whiteSpace: 'pre-wrap', fontSize: '0.8125rem', color: 'var(--color-warning)',
                      }}>{s.last_warning}</pre>
                    )}
                    {s.failing_since && s.status !== 'in_sync' && (
                      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-faint)' }}>
                        since {fmtTime(s.failing_since)}{s.alerted_at ? ' · alert sent' : ''}
                      </div>
                    )}
                  </Td>
                  <Td>
                    <span style={{ color: s.in_sync ? 'var(--color-success)' : 'var(--color-warning)' }}>
                      {s.applied_revision}{s.in_sync ? ' ✓' : ` / ${s.current_revision}`}
                    </span>
                  </Td>
                  <Td>{fmtTime(s.last_contact_at)}</Td>
                  <Td right>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <button className="btn btn-secondary btn-sm" disabled={busy === s.id || !s.enabled}
                        title="Deliver the current revision now"
                        onClick={() => void act(s, () => pushEdgeServer(s.id), s.connection === 'push' ? 'Pushed' : 'Queued for its next poll')}>
                        <Send size={13} />
                      </button>
                      {s.connection === 'push' && (
                        <button className="btn btn-secondary btn-sm" disabled={busy === s.id}
                          title="Ask the agent for its status"
                          onClick={() => void act(s, () => checkEdgeServer(s.id), 'The agent answered')}>
                          <Stethoscope size={13} />
                        </button>
                      )}
                      <button className="btn btn-secondary btn-sm" title="Install command (new link)"
                        disabled={busy === s.id}
                        onClick={() => void act(s, async () => setLink(await newEdgeInstallLink(s.id)))}>
                        <Terminal size={13} />
                      </button>
                      <button className="btn btn-secondary btn-sm" title="Delivery history" onClick={() => setHistory(s)}>
                        <History size={13} />
                      </button>
                      <button className="btn btn-secondary btn-sm" title="Edit" onClick={() => setEditing(s)}>
                        <Pencil size={13} />
                      </button>
                      <button className="btn btn-secondary btn-sm" title="New token" onClick={() => rotate(s)}>
                        <KeyRound size={13} />
                      </button>
                      <button className="btn btn-secondary btn-sm" disabled={busy === s.id}
                        title={s.enabled ? 'Disable (stop delivering)' : 'Enable'}
                        onClick={() => void act(s, () => setEdgeServerEnabled(s.id, !s.enabled))}>
                        <Power size={13} />
                      </button>
                      <button className="btn btn-secondary btn-sm" title="Remove" onClick={() => remove(s)}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </Td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      {editing && (
        <ServerModal
          server={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(r) => {
            setEditing(null)
            if (r) setLink(r)
            void refresh()
          }}
        />
      )}
      {link && <InstallLinkModal data={link} onClose={() => { setLink(null); void refresh() }} />}
      {install && <InstallModal data={install} onClose={() => setInstall(null)} />}
      {history && <HistoryModal server={history} onClose={() => setHistory(null)} />}
    </div>
  )
}

function ServerModal({ server, onClose, onSaved }: {
  server: EdgeServerInfo | null
  onClose: () => void
  onSaved: (link: { server: EdgeServerInfo; install_link: EdgeInstallLink } | null) => void
}) {
  const [f, setF] = useState<Required<EdgeServerInput>>({
    name: server?.name || '',
    description: server?.description || '',
    engine: server?.engine || 'nginx',
    connection: server?.connection || 'pull',
    agent_url: server?.agent_url || '',
    cert_dir: server?.cert_dir || '/etc/twasta-edge',
    is_local: server?.is_local ?? false,
  })
  const [busy, setBusy] = useState(false)
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }))

  const save = async () => {
    setBusy(true)
    try {
      if (server) {
        await updateEdgeServer(server.id, f)
        toast.success('Server updated')
        onSaved(null)
      } else {
        onSaved(await addEdgeServer(f))
      }
    } catch (e) {
      toast.error(errText(e, 'Save failed'))
    } finally { setBusy(false) }
  }

  const where = (local: boolean, title: string, detail: string) => (
    <button type="button" onClick={() => set('is_local', local)} style={{
      flex: 1, textAlign: 'left', padding: '0.6rem 0.7rem', borderRadius: 8, cursor: 'pointer',
      background: f.is_local === local ? 'var(--color-accent-weak)' : 'var(--color-bg-secondary)',
      border: `1px solid ${f.is_local === local ? 'var(--color-accent)' : 'var(--color-border)'}`,
      color: 'var(--color-text)',
    }}>
      <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{title}</div>
      <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>{detail}</div>
    </button>
  )

  return (
    <AdminModal title={server ? `Edit ${server.name}` : 'Add a web server'} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <Field label="Name" hint="How it appears in consoles and alert emails, e.g. platform or oci-web-1.">
          <input autoFocus value={f.name} onChange={(e) => set('name', e.target.value)} style={{ width: '100%' }} />
        </Field>
        <div>
          <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: 4 }}>Where is it?</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {where(true, 'This Twasta server', 'nginx runs on the machine Twasta runs on')}
            {where(false, 'Another machine', 'A separate web server or cluster node')}
          </div>
        </div>
        <details>
          <summary style={{ cursor: 'pointer', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
            Advanced (most installs need none of this)
          </summary>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.75rem' }}>
            <Field label="Description">
              <input value={f.description} onChange={(e) => set('description', e.target.value)}
                placeholder="OCI Mumbai, AD-1" style={{ width: '100%' }} />
            </Field>
            <Field label="Web server">
              <select value={f.engine} onChange={(e) => set('engine', e.target.value as 'nginx' | 'caddy')} style={{ width: '100%' }}>
                <option value="nginx">nginx (installed for you)</option>
                <option value="caddy">Caddy (must already be installed)</option>
              </select>
            </Field>
            <Field label="Connection" hint="Pull needs no address and no inbound port: the agent connects out to Twasta, and changes still arrive within a second.">
              <select value={f.connection} onChange={(e) => set('connection', e.target.value as 'push' | 'pull')} style={{ width: '100%' }}>
                <option value="pull">Pull: the agent connects to Twasta (recommended)</option>
                <option value="push">Push: Twasta calls the agent</option>
              </select>
            </Field>
            {f.connection === 'push' && !f.is_local && (
              <Field label="Agent address" hint="Where Twasta reaches the agent, normally a private address. Allow this port from the Twasta servers only.">
                <input value={f.agent_url} onChange={(e) => set('agent_url', e.target.value)}
                  placeholder="http://10.0.2.5:7443" spellCheck={false} style={{ width: '100%' }} />
              </Field>
            )}
            <Field label="Certificate directory on the server" hint="The agent writes certificates here and refuses to write anywhere else.">
              <input value={f.cert_dir} onChange={(e) => set('cert_dir', e.target.value)} spellCheck={false} style={{ width: '100%' }} />
            </Field>
          </div>
        </details>
      </div>
      <div className="modal-footer" style={{ marginTop: 12 }}>
        <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy || !f.name.trim()}>
          {busy ? 'Saving…' : server ? 'Save' : 'Add and get install command'}
        </button>
      </div>
    </AdminModal>
  )
}

/** The one command, plus live progress until the server is In sync. */
function InstallLinkModal({ data, onClose }: {
  data: { server: EdgeServerInfo; install_link: EdgeInstallLink }; onClose: () => void
}) {
  const [s, setS] = useState<EdgeServerInfo>(data.server)
  useEffect(() => {
    const t = window.setInterval(async () => {
      try {
        const r = await getEdgeServers()
        const me = r.servers.find((x) => x.id === data.server.id)
        if (me) setS(me)
      } catch { /* keep polling */ }
    }, 2000)
    return () => window.clearInterval(t)
  }, [data.server.id])

  const cmd = data.install_link.command
  const review = `curl -fsSL -o twasta-edge-install.sh ${data.install_link.url}\nless twasta-edge-install.sh\nsudo bash twasta-edge-install.sh --dry-run\nsudo bash twasta-edge-install.sh`
  const copy = (v: string) => { void navigator.clipboard.writeText(v).then(() => toast.success('Copied')) }
  const connected = !!s.last_contact_at
  const synced = s.status === 'in_sync'
  const failed = s.status === 'failed' || s.status === 'offline'
  const step = (done: boolean, active: boolean, label: string, detail?: string) => (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: '0.875rem', opacity: done || active ? 1 : 0.5 }}>
      {done ? <Check size={16} style={{ color: 'var(--color-success)' }} />
        : active ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
          : <span style={{ width: 16 }} />}
      <div>
        <div>{label}</div>
        {detail && <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>{detail}</div>}
      </div>
    </div>
  )

  return (
    <AdminModal title={`Connect ${s.name}`} onClose={onClose} wide>
      <p style={{ fontSize: '0.875rem', margin: '0 0 0.5rem' }}>
        {data.install_link.is_local
          ? <>Run this <strong>on the Twasta server itself</strong>, as a user who can use sudo:</>
          : <>Run this on <strong>{s.name}</strong>, as a user who can use sudo:</>}
      </p>
      <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
        <pre style={{
          flex: 1, margin: 0, fontSize: '0.8125rem', background: 'var(--color-bg-secondary)',
          padding: '0.6rem 0.75rem', borderRadius: 6, overflowX: 'auto', whiteSpace: 'pre',
        }}>{cmd}</pre>
        <button className="btn btn-primary btn-sm" onClick={() => copy(cmd)} title="Copy">
          <Copy size={14} />
        </button>
      </div>
      <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0.4rem 0 0' }}>
        It installs nginx and the agent, sets up SELinux and the firewall, and connects the server.
        Your existing nginx sites are left untouched. The link works until{' '}
        {fmtTime(data.install_link.expires_at)} and stops working once the server connects.
      </p>
      <details style={{ marginTop: 6 }}>
        <summary style={{ cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
          Review the script before running it (recommended on a live server)
        </summary>
        <pre style={{ fontSize: '0.8125rem', background: 'var(--color-bg-secondary)', padding: '0.6rem', borderRadius: 6, whiteSpace: 'pre-wrap' }}>
          {review}
        </pre>
        <button className="btn btn-secondary btn-sm" onClick={() => copy(review)}>Copy</button>
      </details>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
        {step(connected, !connected, 'Waiting for the server to connect…', connected ? `Connected from ${s.last_seen_ip || 'the server'}` : undefined)}
        {step(synced, connected && !synced && !failed, 'Applying configuration and certificates',
          synced ? `Revision ${s.applied_revision} applied${s.engine_version ? ` · ${s.engine_version.replace(/^nginx version: /, '')}` : ''}` : undefined)}
        {failed && (
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.8125rem', color: 'var(--color-error)' }}>
            {s.last_error}
          </pre>
        )}
        {synced && s.last_warning && (
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '0.8125rem', color: 'var(--color-warning)' }}>
            nginx warned: {s.last_warning}
          </pre>
        )}
        {synced && <div style={{ fontWeight: 600, color: 'var(--color-success)' }}>✓ {s.name} is in sync</div>}
      </div>
      <div className="modal-footer" style={{ marginTop: 12 }}>
        <button className="btn btn-primary" onClick={onClose}>{synced ? 'Done' : 'Close (keeps waiting in the list)'}</button>
      </div>
    </AdminModal>
  )
}

function InstallModal({ data, onClose }: {
  data: { server: EdgeServerInfo; install: EdgeInstall }; onClose: () => void
}) {
  const copy = (v: string) => { void navigator.clipboard.writeText(v).then(() => toast.success('Copied')) }
  return (
    <AdminModal title={`Install the agent on ${data.server.name}`} onClose={onClose} wide>
      <InfoBox>
        <strong>This token is shown only once.</strong> Copy the configuration now. If it's lost,
        issue a new token with the key button.
      </InfoBox>
      <ol style={{ fontSize: '0.875rem', lineHeight: 1.6, paddingLeft: '1.1rem' }}>
        {data.install.steps.map((s, i) => <li key={i}><code style={{ whiteSpace: 'pre-wrap' }}>{s}</code></li>)}
      </ol>
      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => void saveAgentFile()}>
          <Download size={13} style={{ marginRight: 4, verticalAlign: '-2px' }} />Download agent
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => copy(data.install.agent_json)}>Copy agent.json</button>
      </div>
      <pre style={{
        fontSize: '0.8125rem', background: 'var(--color-bg-secondary)', padding: '0.75rem',
        borderRadius: 6, overflow: 'auto', whiteSpace: 'pre',
      }}>{data.install.agent_json}</pre>
      <div className="modal-footer" style={{ marginTop: 8 }}>
        <button className="btn btn-primary" onClick={onClose}>I have saved it</button>
      </div>
    </AdminModal>
  )
}

const DELIVERY_COLOR: Record<string, string> = {
  success: 'var(--color-success)', failed: 'var(--color-error)', pending: 'var(--color-info)',
  sending: 'var(--color-info)', superseded: 'var(--color-text-faint)',
}

function HistoryModal({ server, onClose }: { server: EdgeServerInfo; onClose: () => void }) {
  const [rows, setRows] = useState<EdgeDeliveryInfo[] | null>(null)
  useEffect(() => {
    getEdgeServerDeliveries(server.id).then(setRows).catch((e) => {
      toast.error(errText(e, 'Failed to load history')); setRows([])
    })
  }, [server.id])
  return (
    <AdminModal title={`Deliveries to ${server.name}`} onClose={onClose} wide>
      {!rows ? <div>Loading…</div> : rows.length === 0 ? <div>Nothing delivered yet.</div> : (
        <table style={tableStyle}>
          <thead><tr><Th>Rev</Th><Th>Change</Th><Th>Status</Th><Th>Attempts</Th><Th>When</Th></tr></thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id}>
                <Td>{d.revision}</Td>
                <Td>
                  {d.summary || '—'}
                  {d.error && d.status === 'failed' && (
                    <pre style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap', fontSize: '0.8125rem', color: 'var(--color-error)' }}>{d.error}</pre>
                  )}
                </Td>
                <Td><span style={{ ...chipStyle, color: DELIVERY_COLOR[d.status] }}>{d.status}</span></Td>
                <Td>{d.attempts}</Td>
                <Td>
                  {fmtTime(d.delivered_at || d.last_attempt_at || d.created_at)}
                  {d.status === 'failed' && d.next_attempt_at && (
                    <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-faint)' }}>retry {fmtTime(d.next_attempt_at)}</div>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="modal-footer" style={{ marginTop: 8 }}>
        <button className="btn btn-secondary" onClick={onClose}>Close</button>
      </div>
    </AdminModal>
  )
}
